import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { ui } from '@wp-g2/styles';
import { memoize } from '@wp-g2/utils';
import { findAll } from 'highlight-words-core';
import { createElement } from 'react';
/**
 * Source:
 * https://github.com/bvaughn/react-highlight-words/blob/master/src/Highlighter.js
 */

/**
 * @typedef Options
 * @property {string} [activeClassName]
 * @property {number} [activeIndex]
 * @property {import('react').AllHTMLAttributes['style']} [activeStyle]
 * @property {boolean} [autoEscape]
 * @property {boolean} [caseSensitive=false]
 * @property {import('react').ReactNode} children
 * @property {import('highlight-words-core').FindAllArgs['findChunks']} [findChunks]
 * @property {string | object} [highlightClassName]
 * @property {import('react').AllHTMLAttributes['style']} [highlightStyle]
 * @property {string} [highlightTag]
 * @property {import('highlight-words-core').FindAllArgs['sanitize']} [sanitize]
 * @property {string[]} [searchWords]
 * @property {string} [unhighlightClassName]
 * @property {import('react').AllHTMLAttributes['style']} [unhighlightStyle]
 */

/**
 *
 * @param {Options} options
 */

export function createHighlighterText(_ref) {
  var _ref$activeClassName = _ref.activeClassName,
      activeClassName = _ref$activeClassName === void 0 ? '' : _ref$activeClassName,
      _ref$activeIndex = _ref.activeIndex,
      activeIndex = _ref$activeIndex === void 0 ? -1 : _ref$activeIndex,
      activeStyle = _ref.activeStyle,
      autoEscape = _ref.autoEscape,
      _ref$caseSensitive = _ref.caseSensitive,
      caseSensitive = _ref$caseSensitive === void 0 ? false : _ref$caseSensitive,
      children = _ref.children,
      findChunks = _ref.findChunks,
      _ref$highlightClassNa = _ref.highlightClassName,
      highlightClassName = _ref$highlightClassNa === void 0 ? '' : _ref$highlightClassNa,
      _ref$highlightStyle = _ref.highlightStyle,
      highlightStyle = _ref$highlightStyle === void 0 ? {} : _ref$highlightStyle,
      _ref$highlightTag = _ref.highlightTag,
      highlightTag = _ref$highlightTag === void 0 ? 'mark' : _ref$highlightTag,
      sanitize = _ref.sanitize,
      _ref$searchWords = _ref.searchWords,
      searchWords = _ref$searchWords === void 0 ? [] : _ref$searchWords,
      _ref$unhighlightClass = _ref.unhighlightClassName,
      unhighlightClassName = _ref$unhighlightClass === void 0 ? '' : _ref$unhighlightClass,
      unhighlightStyle = _ref.unhighlightStyle;
  if (!children) return null;
  if (typeof children !== 'string') return children;
  var textToHighlight = children;
  var chunks = findAll({
    autoEscape: autoEscape,
    caseSensitive: caseSensitive,
    findChunks: findChunks,
    sanitize: sanitize,
    searchWords: searchWords,
    textToHighlight: textToHighlight
  });
  var HighlightTag = highlightTag;
  var highlightIndex = -1;
  var highlightClassNames = '';
  var highlightStyles;
  var memoizedLowercaseProps = memoize(function lowercaseProps(object) {
    var mapped = {};

    for (var key in object) {
      mapped[key.toLowerCase()] = object[key];
    }

    return mapped;
  });
  var textContent = chunks.map(function (chunk, index) {
    var text = textToHighlight.substr(chunk.start, chunk.end - chunk.start);

    if (chunk.highlight) {
      highlightIndex++;
      var highlightClass;

      if (typeof highlightClassName === 'object') {
        if (!caseSensitive) {
          highlightClassName = memoizedLowercaseProps(highlightClassName);
          highlightClass = highlightClassName[text.toLowerCase()];
        } else {
          highlightClass = highlightClassName[text];
        }
      } else {
        highlightClass = highlightClassName;
      }

      var isActive = highlightIndex === +activeIndex;
      highlightClassNames = highlightClass + " " + (isActive ? activeClassName : '');
      highlightStyles = isActive === true && activeStyle != null ? Object.assign({}, highlightStyle, activeStyle) : highlightStyle;

      var props = _objectSpread(_objectSpread({}, ui.$('TextHighlight')), {}, {
        children: text,
        className: highlightClassNames,
        key: index,
        style: highlightStyles
      }); // Don't attach arbitrary props to DOM elements; this triggers React DEV warnings (https://fb.me/react-unknown-prop)
      // Only pass through the highlightIndex attribute for custom components.


      if (typeof HighlightTag !== 'string') {
        props.highlightIndex = highlightIndex;
      }

      return /*#__PURE__*/createElement(HighlightTag, props);
    } else {
      return /*#__PURE__*/createElement('span', _objectSpread(_objectSpread({}, ui.$('Text')), {}, {
        children: text,
        className: unhighlightClassName,
        key: index,
        style: unhighlightStyle
      }));
    }
  });
  return textContent;
}